import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ThemeProvider,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import {RequestTheme} from '../Theme';
import StepperButtons from './StepperButtons';
import StepperLeftNavigation, {StepperItemProps} from './StepperLeftNavigation';
import backIcon from '../../../asset/icons/icon_back.svg';
import {DialogClose} from '../../dialogs/DialogFrame/DialogClose';
import BackNavigationConfirmDialog from '../BackNavigationConfirmDialog';

type StepperProps = {
  defaultFirstStep?: number;
  steps: StepperItemProps[];
  nextLabel?: string;
  activeStepIndex: number;
  onBack: () => void;
  onNext: () => void;
  onStepClick: (index: number) => void;
  onCancel: () => void;
  onSubmit: () => void;
  addBtn?: {
    label: string;
    onClick: () => void;
  };
  children: React.ReactNode;
  disabled?: boolean; // Disable navigation,back button and next button
  isSubmitted: boolean; // Disable navigation and hide the back and next buttons
  loading?: boolean; // Show loader inside next button
  fullWidth?: boolean;
  isLastHide?: boolean;
  title?: string;
  footerLabel?: string;
  setClose?: () => void;
  hideButton?: boolean;
  noPadding?: boolean;
  maxWidth?: number | string;
  maxHeight?: number | string;
  hideCloseButton?: boolean;
  customComponent?: React.ReactNode;
};

export const useStepperState = (defaultStep: number) => {
  const [activeStepIndex, setActiveStepIndex] = useState(defaultStep);
  const onNextStep = useCallback(() => {
    setActiveStepIndex(prevActiveStep => prevActiveStep + 1);
  }, []);
  const onPreviousStep = useCallback(() => {
    setActiveStepIndex(prevActiveStep => prevActiveStep - 1);
  }, []);

  const onStepClick = useCallback((index: number) => {
    setActiveStepIndex(index);
  }, []);
  return {
    activeStepIndex,
    setActiveStepIndex,
    onNextStep,
    onPreviousStep: onPreviousStep,
    onStepClick,
  };
};

export const StepperLayout = ({
  defaultFirstStep,
  steps,
  activeStepIndex,
  nextLabel,
  children,
  onBack,
  onNext,
  onStepClick,
  onCancel,
  onSubmit,
  addBtn,
  disabled,
  isSubmitted,
  loading,
  isLastHide,
  title,
  footerLabel,
  hideButton,
  hideCloseButton,
  maxWidth,
  maxHeight,
  customComponent,
}: StepperProps) => {
  const isFirstStep =
    activeStepIndex === (defaultFirstStep ? defaultFirstStep : 0);
  const isLastStep = activeStepIndex === steps.length - 1;
  const isReviewDisabled = steps[activeStepIndex]?.isReviewDisabled ?? false;
  const isPreviousStepDisabled =
    steps[activeStepIndex]?.previousDisabled ?? false;
  const isNextDisabled = isLastStep
    ? isReviewDisabled
    : steps[activeStepIndex + 1]?.disabled;
  const [confirmCloseDialog, setConfirmCloseDialog] = useState(false);

  const confirmClose = useCallback(() => {
    setConfirmCloseDialog(true);
  }, []);

  useEffect(() => {
    const handleBackButton = (_e: PopStateEvent) => {
      setConfirmCloseDialog(true);
      window.history.pushState(null, document.title, window.location.href);
    };
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', handleBackButton);
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);

  return (
    <Dialog
      maxWidth={isLastHide && isLastStep ? 'lg' : 'xl'}
      fullWidth
      open={true}
      onClose={confirmClose}
      aria-labelledby='stepper-dialog-title'
      PaperProps={{
        sx: {maxWidth: maxWidth, maxHeight: maxHeight},
      }}>
      <ThemeProvider theme={RequestTheme}>
        <DialogTitle id='stepper-dialog-title'>
          {isLastStep && isLastHide ? (
            <IconButton
              title='Close'
              className='w-23 h-18 ml-9 mt-7 mr-4 cursor-pointer'
              onClick={onBack}>
              <img src={backIcon} alt='Asset is loading..' />
            </IconButton>
          ) : null}
          <Typography
            className={`text-white ${isLastStep && isLastHide ? '' : 'ml-9'}`}
            sx={{position: 'relative', zIndex: 1}}>
            {isLastStep && isLastHide
              ? 'Preview'
              : title && title.length
              ? title
              : ''}
          </Typography>
          {hideCloseButton && hideCloseButton === true ? null : (
            <DialogClose onClose={confirmClose} />
          )}
        </DialogTitle>

        <DialogContent sx={{padding: '0 !important'}}>
          <Box sx={{display: 'flex', height: '100%'}}>
            {confirmCloseDialog && !hideCloseButton ? (
              <BackNavigationConfirmDialog
                setDialog={setConfirmCloseDialog}
                closeStepper={onCancel}
              />
            ) : null}
            {isLastHide && isLastStep ? null : (
              <StepperLeftNavigation
                steps={steps}
                activeStepIndex={activeStepIndex}
                onStepClick={onStepClick}
                disabled={disabled || isSubmitted}
              />
            )}
            <Box className='flex-column w-full'>
              <Box className='h-full w-full p-2 overflow-auto scrollbar'>
                {children}
              </Box>
              {!isSubmitted && !hideButton && (
                <DialogActions>
                  <StepperButtons
                    nextLabel={nextLabel}
                    nextDisabled={isNextDisabled}
                    previousDisabled={isPreviousStepDisabled}
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onBack={isFirstStep ? onCancel : onBack}
                    onNext={isLastStep ? onSubmit : onNext}
                    addBtn={addBtn}
                    disabled={disabled}
                    loading={loading}
                    footerLabel={footerLabel}
                    {...(customComponent ? {customComponent} : {})}
                  />
                </DialogActions>
              )}
            </Box>
          </Box>
        </DialogContent>
      </ThemeProvider>
    </Dialog>
  );
};
